import { CAMERA_TYPE } from "@/shared/state/visualization/schema/v1/three-d";

import type { BabylonState } from "../../state";

import { targetFocus } from "./targetFocus";

/**
 * Fraction of the pending accumulator drained per frame in the
 * third-person consume. 0.25 ≈ 4-frame characteristic time (~67ms at
 * 60fps) — smooths the discrete pixel jumps that arrive after OS-side
 * integer rounding into a continuous angular/positional velocity,
 * without the long glide-on-release that an exponential-decay accumulator
 * with a near-1 multiplier (Babylon's `inertia = 0.95`) produces.
 *
 * Lower (closer to 0) → smoother, more lag, more apparent inertia.
 * Higher (closer to 1) → snappier, less smoothing, more visible jitter.
 */
export const SMOOTHING_DRAIN_RATE = 0.25;

/**
 * Floor below which the accumulator is snapped to zero. Without this,
 * exponential decay leaves arbitrarily small residual values that
 * would never reach zero and would keep the accumulator "alive"
 * indefinitely.
 */
const SMOOTHING_EPSILON = 1e-4;

/**
 * Drives the per-frame camera state that downstream render systems
 * read. Runs immediately before `applyRenderOrigin` in the render
 * pipeline so the origin shift, fog, and view matrix all derive from
 * the same authoritative `camera.focusPoint` this frame produces.
 *
 * Orbit: writes `camera.focusPoint` from the tracked entity. Does not
 * touch `state.input.cameraDeltas` — orbit input continues to flow
 * through Babylon's `ArcRotateCameraPointersInput`, which writes
 * `babylonCamera.{alpha, beta, radius}` via `_checkInputs`.
 *
 * Third-person: consumes a fraction (`SMOOTHING_DRAIN_RATE`) of
 * `state.input.cameraDeltas` each frame, leaving the rest in the
 * accumulator. The remainder decays exponentially over subsequent
 * frames, smoothing discrete pixel jumps from slow-drag input into a
 * continuous velocity. An epsilon floor (`SMOOTHING_EPSILON`) snaps
 * the accumulator to zero once drained to negligible levels, so the
 * decay terminates in a bounded number of frames rather than
 * asymptotically. Pan deltas shift `camera.focusPoint.{x, z}`;
 * elevation shifts `camera.focusPoint.y`; alpha/beta/zoom drive
 * Babylon's camera fields directly.
 */
export function updateCamera(state: BabylonState): void {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return;
  }

  if (camera.spec.type === CAMERA_TYPE.orbit) {
    // The tracked entity's *absolute* position — `resolveGlobalPositions` ran
    // earlier this frame. Reading its local position would follow a child's
    // joint offset instead of the link itself. When the target names a node
    // inside the entity's model, that node's position instead.
    //
    // Written straight into `focusPoint` rather than through a copy: nothing in
    // `targetFocus` reads it, so there is no ordering to get wrong, and it is
    // left untouched when the target cannot be resolved.
    targetFocus(state, camera.spec.target, camera.focusPoint);
  } else {
    const deltas = state.input.cameraDeltas;
    const babylonCamera = camera.babylonCamera;
    const alphaConsume = drain(deltas.alpha);
    const betaConsume = drain(deltas.beta);
    const zoomConsume = drain(deltas.zoom);
    const panXConsume = drain(deltas.panX);
    const panZConsume = drain(deltas.panZ);
    const elevateConsume = drain(deltas.elevateY);
    babylonCamera.alpha += alphaConsume;
    babylonCamera.beta += betaConsume;
    // Clamp radius into the scene-derived envelope before the next
    // `babylonCamera.update()` (called by `constrainCamera`) rebuilds
    // the view matrix. Without this, a rapid zoom-in scroll can leave
    // the smoothing accumulator with enough negative magnitude that a
    // single frame's consume drives radius below zero, which builds the
    // matrix with the camera flipped to the opposite side of the focus
    // point — visible as the camera passing through the focus.
    babylonCamera.radius = Math.max(
      Math.min(babylonCamera.radius + zoomConsume, camera.zoom.max),
      camera.zoom.min,
    );
    camera.focusPoint.x += panXConsume;
    camera.focusPoint.z += panZConsume;
    camera.focusPoint.y += elevateConsume;
    deltas.alpha = afterDrain(deltas.alpha, alphaConsume);
    deltas.beta = afterDrain(deltas.beta, betaConsume);
    deltas.zoom = afterDrain(deltas.zoom, zoomConsume);
    deltas.panX = afterDrain(deltas.panX, panXConsume);
    deltas.panZ = afterDrain(deltas.panZ, panZConsume);
    deltas.elevateY = afterDrain(deltas.elevateY, elevateConsume);
  }
}

function drain(value: number): number {
  return value * SMOOTHING_DRAIN_RATE;
}

function afterDrain(value: number, consumed: number): number {
  const r = value - consumed;
  return Math.abs(r) < SMOOTHING_EPSILON ? 0 : r;
}
